import React, { useState, useMemo, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import StatusFlagButtons from '@/components/common/StatusFlagButtons';
import ArchiveConfirmDialog from '@/components/common/ArchiveConfirmDialog';
import AddEditTaskDialog from '@/components/tasks/AddEditTaskDialog';
import ReactMarkdown from 'react-markdown';
import { format } from 'date-fns';
import {
  KanbanSquare, Plus, Filter, Users,
  Edit2, Trash2, Archive, Bot, ExternalLink,
  Target, Layers, LayoutGrid, Calendar as CalendarIcon,
  Check, X, AlertTriangle, MoreHorizontal,
  ArrowRight, Zap, ChevronRight, Search } from
'lucide-react';

// ─── Constants ───────────────────────────────────────────────────────────────

const KANBAN_STAGES = [
{ key: 'backlog', label: 'Backlog', icon: Layers, color: 'slate', gradient: 'from-slate-500/20 to-slate-600/10', border: 'border-slate-500/30', badge: 'bg-slate-500/20 text-slate-300' },
{ key: 'planned', label: 'Planned', icon: CalendarIcon, color: 'blue', gradient: 'from-blue-500/20 to-blue-600/10', border: 'border-blue-500/30', badge: 'bg-blue-500/20 text-blue-300' },
{ key: 'in_progress', label: 'In Progress', icon: Zap, color: 'amber', gradient: 'from-amber-500/20 to-amber-600/10', border: 'border-amber-500/30', badge: 'bg-amber-500/20 text-amber-300' },
{ key: 'done', label: 'Done', icon: Check, color: 'emerald', gradient: 'from-emerald-500/20 to-emerald-600/10', border: 'border-emerald-500/30', badge: 'bg-emerald-500/20 text-emerald-300' }];


const GROUP_MODES = [
{ key: 'flat', label: 'No Grouping', icon: LayoutGrid },
{ key: 'officer', label: 'By Officer', icon: Users },
{ key: 'activity', label: 'By Activity', icon: Target }];


const FLAG_COLORS = {
  green: { bg: 'bg-emerald-500', ring: 'ring-emerald-500/40', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  yellow: { bg: 'bg-amber-500', ring: 'ring-amber-500/40', text: 'text-amber-400', dot: 'bg-amber-400' },
  red: { bg: 'bg-red-500', ring: 'ring-red-500/40', text: 'text-red-400', dot: 'bg-red-400' },
  gray: { bg: 'bg-slate-500', ring: 'ring-slate-500/40', text: 'text-slate-400', dot: 'bg-slate-500' }
};

// Derive kanban_stage for tasks that don't have one yet (migration helper)
function deriveKanbanStage(task) {
  if (task.kanban_stage) return task.kanban_stage;
  if (task.archived || task.status_flag === 'green') return 'done';
  if (task.status_flag === 'yellow' || task.status_flag === 'red') return 'in_progress';
  if (task.date) return 'planned';
  return 'backlog';
}


// ─── Compact Kanban Card ─────────────────────────────────────────────────────

function KanbanCard({ task, activity, officer, officers, onEdit, onDetail, onFlagChange, onArchive, onDelete, isDragging }) {
  const flagColor = FLAG_COLORS[task.status_flag] || FLAG_COLORS.gray;
  const activityCode = activity?.activity_code || '—';
  const officerProfile = officer || officers?.find((o) => o.user_email === task.user_email);
  const callsign = officerProfile?.callsign || task.user_email?.split('@')[0] || '?';
  const [showActions, setShowActions] = useState(false);

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', task.id);
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.classList.add('opacity-40', 'scale-95');
      }}
      onDragEnd={(e) => {
        e.currentTarget.classList.remove('opacity-40', 'scale-95');
      }}
      className={`group relative transition-all duration-200 cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-40 scale-95' : ''}`}>

      <Card
        onClick={() => onDetail(task)}
        className="glass-panel border-slate-700/60 hover:border-slate-600 transition-all overflow-hidden cursor-pointer hover:shadow-lg hover:shadow-black/20">

        {/* Flag color bar */}
        <div className={`h-1.5 ${flagColor.bg}`} />

        <CardContent className="p-2.5">
          {/* Top row: activity code + officer + flag dot */}
          <div className="flex items-center justify-between gap-1.5 mb-1.5">
            <div className="flex items-center gap-1.5 min-w-0">
              <Badge className="bg-indigo-600/80 text-white font-mono text-[10px] px-1.5 py-0 h-4 shrink-0">
                {activityCode}
              </Badge>
              {task.task_order &&
              <span className="text-[10px] text-slate-500 font-bold">#{task.task_order}</span>
              }
              {task.generated_by_ai && <Bot className="w-3 h-3 text-purple-400 shrink-0" />}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <div className={`w-2 h-2 rounded-full ${flagColor.dot}`} title={`Status: ${task.status_flag}`} />
              <button
                onClick={(e) => {e.stopPropagation();setShowActions(!showActions);}}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-slate-700/50 rounded">

                <MoreHorizontal className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Task description */}
          <p className="text-slate-700 mb-1.5 text-xs leading-snug line-clamp-2">
            {task.task_description}
          </p>

          {/* Bottom row: officer + date + time */}
          <div className="flex items-center justify-between gap-1">
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-[10px] text-slate-500 truncate max-w-[80px]">{callsign}</span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-slate-800 border-slate-700 text-xs">
                  {task.user_email}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="flex items-center gap-1.5">
              {task.date &&
              <span className="text-[10px] text-slate-500">
                  {format(new Date(task.date + 'T00:00:00'), 'MMM d')}
                </span>
              }
              {task.time_allocation_percent > 0 &&
              <span className="text-[10px] text-slate-600">{task.time_allocation_percent}%</span>
              }
              {(task.xp_value > 0 || task.ep_value > 0) &&
              <span className="text-[10px] text-indigo-400 font-mono">
                  {task.xp_value > 0 ? `${task.xp_value}xp` : ''}{task.ep_value > 0 ? ` ${task.ep_value}ep` : ''}
                </span>
              }
            </div>
          </div>

          {/* Tags row */}
          {task.tags?.length > 0 &&
          <div className="flex flex-wrap gap-0.5 mt-1.5">
              {task.tags.slice(0, 2).map((tag, i) =>
            <span key={i} className="text-[9px] bg-slate-800/80 text-slate-400 px-1 py-0 rounded">
                  {tag.replace(/\s*\([^)]+\)$/, '')}
                </span>
            )}
              {task.tags.length > 2 &&
            <span className="text-[9px] text-slate-500">+{task.tags.length - 2}</span>
            }
            </div>
          }
        </CardContent>
      </Card>

      {/* Quick actions dropdown */}
      {showActions &&
      <div className="absolute right-1 top-8 z-30 bg-slate-800 border border-slate-700 rounded-md shadow-xl py-1 min-w-[120px]"
      onMouseLeave={() => setShowActions(false)}>

          <button onClick={(e) => {e.stopPropagation();onEdit(task);setShowActions(false);}}
        className="w-full text-left px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-700/50 flex items-center gap-2">
            <Edit2 className="w-3 h-3" /> Edit
          </button>
          <div className="px-3 py-1.5">
            <div className="text-[10px] text-slate-500 mb-1">Status Flag</div>
            <StatusFlagButtons
            currentFlag={task.status_flag}
            onChange={(flag) => {onFlagChange(task.id, flag);setShowActions(false);}}
            includeGray={true}
            size="xs" />

          </div>
          <button onClick={(e) => {e.stopPropagation();onArchive(task);setShowActions(false);}}
        className="w-full text-left px-3 py-1.5 text-xs text-amber-400 hover:bg-slate-700/50 flex items-center gap-2">
            <Archive className="w-3 h-3" /> Archive
          </button>
          <button onClick={(e) => {e.stopPropagation();onDelete(task);setShowActions(false);}}
        className="w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-slate-700/50 flex items-center gap-2">
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      }
    </div>);

}


// ─── Kanban Column ───────────────────────────────────────────────────────────

function KanbanColumn({ stage, tasks, allActivities, officers, groupMode, missions, objectives, onMoveTask, onEditTask, onDetailTask, onFlagChange, onArchive, onDelete, onAddTask }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const stageConfig = KANBAN_STAGES.find((s) => s.key === stage);
  const Icon = stageConfig.icon;

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!isDragOver) setIsDragOver(true);
  };
  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const taskId = e.dataTransfer.getData('text/plain');
    if (taskId) onMoveTask(taskId, stage);
  };

  // Group tasks
  const groupedTasks = useMemo(() => {
    if (groupMode === 'flat') return { '_all': tasks };
    if (groupMode === 'officer') {
      const groups = {};
      tasks.forEach((t) => {
        const key = t.user_email || 'unassigned';
        if (!groups[key]) groups[key] = [];
        groups[key].push(t);
      });
      return groups;
    }
    if (groupMode === 'activity') {
      const groups = {};
      tasks.forEach((t) => {
        const act = allActivities.find((a) => a.id === t.activity_id);
        const key = act ? `${act.activity_code} — ${act.title}` : 'No Activity';
        if (!groups[key]) groups[key] = [];
        groups[key].push(t);
      });
      return groups;
    }
    return { '_all': tasks };
  }, [tasks, groupMode, allActivities]);

  const getGroupLabel = (key) => {
    if (key === '_all') return null;
    if (groupMode === 'officer') {
      const off = officers.find((o) => o.user_email === key);
      return off ? `${off.callsign} (${off.rank_title})` : key;
    }
    return key;
  };

  return (
    <div
      className={`flex flex-col min-w-[280px] w-[280px] lg:w-full lg:min-w-0 rounded-xl transition-all duration-200 ${
      isDragOver ? `ring-2 ${stageConfig.border} bg-gradient-to-b ${stageConfig.gradient} shadow-lg` : 'bg-slate-900/40'}`
      }
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>

      {/* Column header */}
      <div className={`flex items-center justify-between px-3 py-2.5 border-b border-slate-800/60`}>
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${stageConfig.badge.split(' ')[1]}`} />
          <span className="text-sm font-semibold text-slate-200">{stageConfig.label}</span>
          <Badge className={`${stageConfig.badge} text-[10px] px-1.5 py-0 h-4 font-mono`}>
            {tasks.length}
          </Badge>
        </div>
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onAddTask(stage)}
          className="h-6 w-6 text-slate-400 hover:text-white hover:bg-slate-700/50"
          title={`Add task to ${stageConfig.label}`}>

          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Cards area */}
      <ScrollArea className="flex-1 px-2 py-2" style={{ maxHeight: 'calc(100vh - 220px)' }}>
        <div className="space-y-1.5">
          {Object.entries(groupedTasks).map(([groupKey, groupTasks]) =>
          <div key={groupKey}>
              {getGroupLabel(groupKey) &&
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-1 py-1.5 mt-1 first:mt-0 border-b border-slate-800/40 mb-1">
                  {getGroupLabel(groupKey)}
                </div>
            }
              {groupTasks.map((task) =>
            <KanbanCard
              key={task.id}
              task={task}
              activity={allActivities.find((a) => a.id === task.activity_id)}
              officers={officers}
              onEdit={onEditTask}
              onDetail={onDetailTask}
              onFlagChange={onFlagChange}
              onArchive={onArchive}
              onDelete={onDelete} />

            )}
            </div>
          )}
          {tasks.length === 0 &&
          <div className="text-center py-8 text-slate-600 text-xs">
              {isDragOver ?
            <div className="flex flex-col items-center gap-1">
                  <ArrowRight className="w-5 h-5 animate-pulse" />
                  <span>Drop here</span>
                </div> :

            <span>No tasks</span>
            }
            </div>
          }
        </div>
      </ScrollArea>
    </div>);

}



// ─── Task Detail Dialog ──────────────────────────────────────────────────────

function TaskDetailDialog({ open, task, onClose, allActivities, officers, missions, objectives, onEdit, onFlagChange, onMoveTask }) {
  if (!task) return null;
  const activity = allActivities.find((a) => a.id === task.activity_id);
  const objective = objectives.find((o) => o.id === activity?.objective_id);
  const mission = missions.find((m) => m.id === objective?.mission_id);
  const officer = officers.find((o) => o.user_email === task.user_email);
  const flagColor = FLAG_COLORS[task.status_flag] || FLAG_COLORS.gray;
  const currentStage = deriveKanbanStage(task);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tasks-taskboard2-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Task Details
            {task.generated_by_ai && <Bot className="w-4 h-4 text-purple-400" />}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <div className="text-xs text-slate-500 mb-1">Description</div>
            <p className="text-slate-200">{task.task_description}</p>
          </div>

          {task.key_result_focus &&
          <div>
              <div className="text-xs text-slate-500 mb-1">Key Result Focus</div>
              <p className="text-slate-300 text-sm">{task.key_result_focus}</p>
            </div>
          }

          {/* Breadcrumb: Mission > Objective > Activity */}
          {activity &&
          <div className="flex items-center gap-1.5 flex-wrap text-xs">
              {mission && <Badge variant="outline" className="border-purple-500/30 text-purple-300 text-[10px]">{mission.title}</Badge>}
              {mission && objective && <ChevronRight className="w-3 h-3 text-slate-600" />}
              {objective && <Badge variant="outline" className="border-cyan-500/30 text-cyan-300 text-[10px]">{objective.title}</Badge>}
              {objective && <ChevronRight className="w-3 h-3 text-slate-600" />}
              <Badge className="bg-indigo-600/80 text-white text-[10px]">[{activity.activity_code}] {activity.title}</Badge>
            </div>
          }

          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-xs text-slate-500 mb-1">Officer</div>
              <span className="text-sm text-slate-300">{officer?.callsign || task.user_email}</span>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Date</div>
              <span className="text-sm text-slate-300">{task.date ? format(new Date(task.date + 'T00:00:00'), 'MMM d, yyyy') : 'No date'}</span>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Time</div>
              <span className="text-sm text-slate-300">{task.time_allocation_percent}%</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-slate-500 mb-1">Status Flag</div>
              <StatusFlagButtons
                currentFlag={task.status_flag}
                onChange={(flag) => onFlagChange(task.id, flag)}
                includeGray={true}
                size="sm" />

            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Kanban Stage</div>
              <Select value={currentStage} onValueChange={(v) => onMoveTask(task.id, v)}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-xs h-8 w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {KANBAN_STAGES.map((s) =>
                  <SelectItem key={s.key} value={s.key} className="text-xs">{s.label}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tags */}
          {task.tags?.length > 0 &&
          <div>
              <div className="text-xs text-slate-500 mb-1">Tags</div>
              <div className="flex flex-wrap gap-1">
                {task.tags.map((tag, i) =>
              <Badge key={i} variant="outline" className="border-slate-700 text-slate-300 text-[10px]">{tag}</Badge>
              )}
              </div>
            </div>
          }

          {/* Gamification */}
          {(task.xp_value > 0 || task.ep_value > 0 || task.category) &&
          <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/40">
              <div className="text-xs text-slate-500 mb-1">Gamification</div>
              <div className="flex items-center gap-3 text-sm">
                {task.category && <Badge className="bg-purple-500/20 text-purple-300 text-[10px]">{task.category}</Badge>}
                {task.xp_value > 0 && <span className="text-indigo-400 font-mono text-xs">{task.xp_value} XP</span>}
                {task.ep_value > 0 && <span className="text-amber-400 font-mono text-xs">{task.ep_value} EP</span>}
              </div>
            </div>
          }

          {/* Bot execution result */}
          {task.bot_execution_result &&
          <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-semibold text-purple-300">AI Execution Report</span>
              </div>
              <div className="text-sm text-slate-300 leading-relaxed bg-purple-500/5 p-3 rounded border border-purple-500/20 prose prose-invert prose-sm max-w-none">
                <ReactMarkdown>{task.bot_execution_result}</ReactMarkdown>
              </div>
            </div>
          }

          {/* Uploaded files */}
          {task.uploaded_files_meta?.length > 0 &&
          <div>
              <div className="text-xs text-slate-500 mb-2">Generated Files</div>
              <div className="flex flex-wrap gap-2">
                {task.uploaded_files_meta.map((file, idx) =>
              <a key={idx} href={file.url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 text-xs hover:bg-cyan-500/20 transition-colors">
                    <ExternalLink className="w-3 h-3" /> {file.filename}
                  </a>
              )}
              </div>
            </div>
          }

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-800">
            <Button data-testid="tasks-edit-in-daily-tasks-btn" onClick={() => {onEdit(task);onClose();}} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 text-xs">
              <Edit2 className="w-3 h-3 mr-1" /> Edit in Daily Tasks
            </Button>
            <Link to={createPageUrl('DailyTasks') + `?taskId=${task.id}&openModal=taskDetails&date=${task.date || ''}&user=${task.user_email}`}>
              <Button variant="outline" className="border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 text-xs">
                <ExternalLink className="w-3 h-3 mr-1" /> Open in Daily Tasks
              </Button>
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>);

}


// ─── Main TaskBoard Page ─────────────────────────────────────────────────────

export default function TaskBoard2() {
  const queryClient = useQueryClient();
  const location = useLocation();

  // State
  const [groupMode, setGroupMode] = useState('flat');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({ officers: [], missions: [], objectives: [], activities: [], tags: [], flagFilter: 'all', showArchived: false });
  const [searchQuery, setSearchQuery] = useState('');
  const [addDialog, setAddDialog] = useState({ open: false, stage: 'backlog' });
  const [detailDialog, setDetailDialog] = useState({ open: false, task: null });
  const [archiveDialog, setArchiveDialog] = useState({ open: false, task: null });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Data queries
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: () => base44.auth.me() });
  const { data: myProfile } = useQuery({
    queryKey: ['myProfile', user?.email],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find((p) => p.user_email === user?.email);
    },
    enabled: !!user?.email
  });
  const isCommander = user?.role === 'admin' || myProfile?.custom_role === 'Commander';

  const { data: dailyTasks = [] } = useQuery({
    queryKey: ['dailyTasks', user?.email],
    queryFn: () => base44.entities.DailyTask.list(),
    enabled: !!user?.email
  });

  const { data: allActivities = [] } = useQuery({
    queryKey: ['allActivities'],
    queryFn: () => base44.entities.Activity.list(),
    initialData: []
  });

  const { data: officers = [] } = useQuery({
    queryKey: ['officers'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    initialData: []
  });

  const { data: missions = [] } = useQuery({
    queryKey: ['missions'],
    queryFn: () => base44.entities.Mission.list(),
    initialData: []
  });

  const { data: objectives = [] } = useQuery({
    queryKey: ['objectives'],
    queryFn: () => base44.entities.Objective.list(),
    initialData: []
  });

  // Mutations
  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.DailyTask.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dailyTasks'] })
  });

  const createTaskMutation = useMutation({
    mutationFn: (data) => base44.entities.DailyTask.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dailyTasks'] })
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (id) => base44.entities.DailyTask.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dailyTasks'] })
  });

  // Handlers
  const handleMoveTask = useCallback((taskId, newStage) => {
    const task = dailyTasks.find((t) => t.id === taskId);
    if (!task) return;

    const currentStage = deriveKanbanStage(task);
    if (currentStage === newStage) return;

    const updates = { kanban_stage: newStage };

    // Smart auto-transitions
    if (newStage === 'in_progress' && task.status_flag === 'gray') {
      updates.status_flag = 'yellow';
    }
    if (newStage === 'done') {
      updates.status_flag = 'green';
    }
    if (newStage === 'backlog') {


      // De-schedule: don't clear date, just move the stage
      // User can clear date manually if they want
    }updateTaskMutation.mutate({ id: taskId, data: updates });
  }, [dailyTasks, updateTaskMutation]);

  const handleFlagChange = useCallback((taskId, newFlag) => {
    updateTaskMutation.mutate({ id: taskId, data: { status_flag: newFlag } });
  }, [updateTaskMutation]);

  const handleCreateTask = useCallback((taskData, editingTask) => {
    if (editingTask) {
      // Edit mode (if ever used from TaskBoard)
      updateTaskMutation.mutate({
        id: editingTask.id,
        data: { ...editingTask, ...taskData }
      });
    } else {
      // The shared dialog already assembled the full task data object
      // including user_email, status, date, gamification fields, etc.
      // Just ensure kanban_stage is set
      if (!taskData.kanban_stage) {
        taskData.kanban_stage = 'backlog';
      }
      createTaskMutation.mutate(taskData);
    }
  }, [createTaskMutation, updateTaskMutation]);

  const handleArchiveTask = useCallback((task) => {
    updateTaskMutation.mutate({
      id: task.id,
      data: { archived: true, archived_at: new Date().toISOString(), kanban_stage: 'done' }
    });
    setArchiveDialog({ open: false, task: null });
  }, [updateTaskMutation]);

  const handleDeleteTask = useCallback((task) => {
    if (deleteConfirm === task.id) {
      deleteTaskMutation.mutate(task.id);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(task.id);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  }, [deleteConfirm, deleteTaskMutation]);

  const handleEditInDailyTasks = useCallback((task) => {
    window.location.href = createPageUrl('DailyTasks') + `?taskId=${task.id}&date=${task.date || format(new Date(), 'yyyy-MM-dd')}&user=${task.user_email}`;
  }, []);

  // Filter tasks
  const filteredTasks = useMemo(() => {
    return dailyTasks.filter((task) => {
      // Exclude tentative/rejected from board (same as DailyTaskPlanner)
      if (task.status === 'tentative' || task.status === 'rejected') return false;

      // Archive filter
      if (!filters.showArchived && task.archived) return false;

      // Officer filter: if no officers selected, show based on role
      if (filters.officers.length > 0) {
        if (!filters.officers.includes(task.user_email)) return false;
      } else if (!isCommander) {
        // Non-commanders only see their own + squad
        const squadMembers = myProfile?.squad_members || [];
        if (task.user_email !== user?.email && !squadMembers.includes(task.user_email)) return false;
      }

      // Mission filter
      if (filters.missions.length > 0) {
        const activity = allActivities.find((a) => a.id === task.activity_id);
        const objective = objectives.find((o) => o.id === activity?.objective_id);
        if (!objective || !filters.missions.includes(objective.mission_id)) return false;
      }

      // Objective filter
      if (filters.objectives.length > 0) {
        const activity = allActivities.find((a) => a.id === task.activity_id);
        if (!activity || !filters.objectives.includes(activity.objective_id)) return false;
      }

      // Activity filter
      if (filters.activities.length > 0) {
        if (!filters.activities.includes(task.activity_id)) return false;
      }

      // Tag filter
      if (filters.tags.length > 0) {
        if (!task.tags?.some((tag) => filters.tags.includes(tag))) return false;
      }

      // Flag filter
      if (filters.flagFilter !== 'all' && task.status_flag !== filters.flagFilter) return false;

      // Search
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const activity = allActivities.find((a) => a.id === task.activity_id);
        const officer = officers.find((o) => o.user_email === task.user_email);
        const searchFields = [
        task.task_description,
        task.key_result_focus,
        activity?.activity_code,
        activity?.title,
        officer?.callsign,
        task.user_email,
        ...(task.tags || [])].
        filter(Boolean).join(' ').toLowerCase();
        if (!searchFields.includes(q)) return false;
      }

      return true;
    });
  }, [dailyTasks, filters, searchQuery, isCommander, user, myProfile, allActivities, objectives, officers]);

  // Group into columns
  const columnTasks = useMemo(() => {
    const columns = { backlog: [], planned: [], in_progress: [], done: [] };
    filteredTasks.forEach((task) => {
      const stage = deriveKanbanStage(task);
      if (columns[stage]) columns[stage].push(task);
    });
    // Sort within columns: by task_order, then date
    Object.values(columns).forEach((arr) => {
      arr.sort((a, b) => {
        if (a.task_order !== b.task_order) return (a.task_order || 99) - (b.task_order || 99);
        if (a.date && b.date) return a.date.localeCompare(b.date);
        if (a.date) return -1;
        return 1;
      });
    });
    return columns;
  }, [filteredTasks]);

  // Stats
  const totalVisible = filteredTasks.length;
  const totalAll = dailyTasks.filter((t) => t.status !== 'tentative' && t.status !== 'rejected').length;

  // Active filter count
  const activeFilterCount = filters.officers.length + filters.missions.length + filters.objectives.length + filters.activities.length + filters.tags.length + (filters.flagFilter !== 'all' ? 1 : 0) + (filters.showArchived ? 1 : 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 -m-8 p-4 lg:p-6">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <KanbanSquare className="w-7 h-7 text-indigo-400" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-100">Task Board</h1>
              <p className="text-xs text-slate-500">
                {totalVisible} task{totalVisible !== 1 ? 's' : ''} visible
                {totalVisible !== totalAll && ` of ${totalAll} total`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input data-testid="tasks-taskboard2-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tasks..."
                className="bg-slate-800/50 border-slate-700 text-slate-200 text-xs h-8 pl-8 w-48" />

            </div>

            {/* Group mode */}
            <div className="flex items-center bg-slate-800/50 rounded-md border border-slate-700/60">
              {GROUP_MODES.map((mode) =>
              <button
                key={mode.key}
                onClick={() => setGroupMode(mode.key)}
                className={`px-2.5 py-1.5 text-xs flex items-center gap-1.5 transition-colors ${
                groupMode === mode.key ?
                'bg-indigo-600/20 text-indigo-300 border-indigo-500/30' :
                'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'} ${
                mode.key === 'flat' ? 'rounded-l-md' : mode.key === 'activity' ? 'rounded-r-md' : ''}`}
                title={mode.label}>

                  <mode.icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{mode.label}</span>
                </button>
              )}
            </div>

            {/* Filters toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`h-8 border-slate-700 text-xs ${activeFilterCount > 0 ? 'bg-indigo-600/20 border-indigo-500/30 text-indigo-300' : 'text-slate-400 hover:text-slate-200'}`}>

              <Filter className="w-3.5 h-3.5 mr-1" />
              Filters
              {activeFilterCount > 0 &&
              <Badge className="ml-1 bg-indigo-600 text-white text-[10px] px-1 py-0 h-4">{activeFilterCount}</Badge>
              }
            </Button>
          </div>
        </div>

        {/* Filters panel */}
        {filtersOpen &&
        <div className="mt-3 p-3 bg-slate-900/80 border border-slate-800 rounded-lg space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {/* Officer filter */}
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Officers</label>
                <Select
                value={filters.officers.length === 0 ? 'all' : filters.officers[0]}
                onValueChange={(v) => setFilters((f) => ({ ...f, officers: v === 'all' ? [] : [v] }))}>

                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-xs h-8">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
                    <SelectItem value="all" className="text-xs">All Officers</SelectItem>
                    <SelectItem value={user?.email} className="text-xs">Just Me</SelectItem>
                    {officers.filter((o) => o.user_email !== user?.email).map((o) =>
                  <SelectItem key={o.id} value={o.user_email} className="text-xs">{o.callsign}</SelectItem>
                  )}
                  </SelectContent>
                </Select>
              </div>

              {/* Mission filter */}
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Mission</label>
                <Select
                value={filters.missions.length === 0 ? 'all' : filters.missions[0]}
                onValueChange={(v) => setFilters((f) => ({ ...f, missions: v === 'all' ? [] : [v], objectives: [], activities: [] }))}>

                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-xs h-8">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
                    <SelectItem value="all" className="text-xs">All Missions</SelectItem>
                    {missions.filter((m) => m.status !== 'archived').map((m) =>
                  <SelectItem key={m.id} value={m.id} className="text-xs">{m.title}</SelectItem>
                  )}
                  </SelectContent>
                </Select>
              </div>

              {/* Objective filter */}
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Objective</label>
                <Select
                value={filters.objectives.length === 0 ? 'all' : filters.objectives[0]}
                onValueChange={(v) => setFilters((f) => ({ ...f, objectives: v === 'all' ? [] : [v], activities: [] }))}>

                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-xs h-8">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
                    <SelectItem value="all" className="text-xs">All Objectives</SelectItem>
                    {objectives.
                  filter((o) => o.status !== 'archived' && (!filters.missions.length || filters.missions.includes(o.mission_id))).
                  map((o) =>
                  <SelectItem key={o.id} value={o.id} className="text-xs">{o.title}</SelectItem>
                  )}
                  </SelectContent>
                </Select>
              </div>

              {/* Activity filter */}
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Activity</label>
                <Select
                value={filters.activities.length === 0 ? 'all' : filters.activities[0]}
                onValueChange={(v) => setFilters((f) => ({ ...f, activities: v === 'all' ? [] : [v] }))}>

                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-xs h-8">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
                    <SelectItem value="all" className="text-xs">All Activities</SelectItem>
                    {allActivities.
                  filter((a) => a.status !== 'archived' && (!filters.objectives.length || filters.objectives.includes(a.objective_id))).
                  map((a) =>
                  <SelectItem key={a.id} value={a.id} className="text-xs">[{a.activity_code}] {a.title}</SelectItem>
                  )}
                  </SelectContent>
                </Select>
              </div>

              {/* Flag filter */}
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Flag</label>
                <Select value={filters.flagFilter} onValueChange={(v) => setFilters((f) => ({ ...f, flagFilter: v }))}>
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-xs h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-xs">All Flags</SelectItem>
                    <SelectItem value="green" className="text-xs">🟢 Conquered</SelectItem>
                    <SelectItem value="yellow" className="text-xs">🟡 In Progress</SelectItem>
                    <SelectItem value="red" className="text-xs">🔴 Struggling</SelectItem>
                    <SelectItem value="gray" className="text-xs">⚪ Not Started</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Show archived */}
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Archived</label>
                <Select value={filters.showArchived ? 'yes' : 'no'} onValueChange={(v) => setFilters((f) => ({ ...f, showArchived: v === 'yes' }))}>
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-xs h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="no" className="text-xs">Hide Archived</SelectItem>
                    <SelectItem value="yes" className="text-xs">Show Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Clear filters */}
            {activeFilterCount > 0 &&
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilters({ officers: [], missions: [], objectives: [], activities: [], tags: [], flagFilter: 'all', showArchived: false })}
            className="text-xs text-slate-400 hover:text-white">

                <X className="w-3 h-3 mr-1" /> Clear all filters
              </Button>
          }
          </div>
        }
      </div>

      {/* Kanban Board */}
      <div className="flex gap-3 overflow-x-auto pb-4 lg:grid lg:grid-cols-4 lg:overflow-x-visible">
        {KANBAN_STAGES.map((stage) =>
        <KanbanColumn
          key={stage.key}
          stage={stage.key}
          tasks={columnTasks[stage.key] || []}
          allActivities={allActivities}
          officers={officers}
          missions={missions}
          objectives={objectives}
          groupMode={groupMode}
          onMoveTask={handleMoveTask}
          onEditTask={handleEditInDailyTasks}
          onDetailTask={(task) => setDetailDialog({ open: true, task })}
          onFlagChange={handleFlagChange}
          onArchive={(task) => setArchiveDialog({ open: true, task })}
          onDelete={handleDeleteTask}
          onAddTask={(stage) => setAddDialog({ open: true, stage })} />

        )}
      </div>

      {/* Dialogs */}
      <AddEditTaskDialog
        open={addDialog.open}
        onClose={() => setAddDialog({ open: false, stage: 'backlog' })}
        onSubmit={handleCreateTask}
        user={user}
        officers={officers}
        allActivities={allActivities}
        missions={missions}
        objectives={objectives}
        kanbanStage={addDialog.stage}
        dateRequired={addDialog.stage !== 'backlog'}
        showDatePicker={true}
        titleOverride={`Add Task → ${KANBAN_STAGES.find(s => s.key === addDialog.stage)?.label || 'Backlog'}`}
      />


      <TaskDetailDialog
        open={detailDialog.open}
        task={detailDialog.task}
        onClose={() => setDetailDialog({ open: false, task: null })}
        allActivities={allActivities}
        officers={officers}
        missions={missions}
        objectives={objectives}
        onEdit={handleEditInDailyTasks}
        onFlagChange={handleFlagChange}
        onMoveTask={handleMoveTask} />


      <ArchiveConfirmDialog
        open={archiveDialog.open}
        onOpenChange={(open) => !open && setArchiveDialog({ open: false, task: null })}
        onConfirm={() => handleArchiveTask(archiveDialog.task)}
        itemType="task"
        itemTitle={archiveDialog.task?.task_description?.slice(0, 80)} />

    </div>);

}